import { useCallback, useEffect, useState } from 'react'
import type { PddMarketingOpportunity, PddProductRecord, PinduoduoData, PinduoduoOrderFacts } from './pinduoduo'
import type { PinduoduoAfterSalesFacts } from './pinduoduoAfterSales'
import type { DouyinLiveAnalysis } from './douyinTypes'
import { DOUYIN_SHUIJU_STORE_ID, douyinLiveAnalysisUrl } from './douyinStore'
import type { JdAfterSalesFacts, JdOrderFacts } from './jdOrders'
import type { CompanyProductOverrides, ErpInventory, InternalProductInfo, PlatformProductMappings, ProductImageOverrides, StoreCostProfiles } from './types'

export const PLATFORM_REGISTRY_URL = '/data/platform-registry.json'
export const JD_METADATA_URL = '/data/jd-metadata.json'
export const JD_PRODUCT_CATALOG_URL = '/data/jd-product-catalog.json'
export const JD_ORDER_FACTS_URL = '/data/jd-order-facts.json'
export const JD_AFTER_SALES_FACTS_URL = '/data/jd-after-sales-facts.json'
export const PINDUODUO_DATA_URL = '/data/pinduoduo-data.json'
export const PINDUODUO_PRODUCT_CATALOG_URL = '/data/pinduoduo-product-catalog.json'
export const PINDUODUO_ORDER_FACTS_URL = '/data/pinduoduo-order-facts.json'
export const PINDUODUO_AFTER_SALES_FACTS_URL = '/data/pinduoduo-after-sales-facts.json'
export const PLATFORM_PRODUCT_MAPPINGS_URL = '/data/platform-product-mappings.json'
export const STORE_COST_PROFILES_URL = '/data/store-cost-profiles.json'
export const INTERNAL_PRODUCT_INFO_URL = '/data/internal-product-info.json'
export const COMPANY_PRODUCT_OVERRIDES_URL = '/data/company-product-overrides.json'
export const PRODUCT_IMAGE_OVERRIDES_URL = '/data/product-image-overrides.json'
export const ERP_INVENTORY_URL = '/data/erp-inventory.json'
export const DOUYIN_LIVE_ANALYSIS_URL = douyinLiveAnalysisUrl(DOUYIN_SHUIJU_STORE_ID)

export type CapabilityAvailability = 'ready' | 'partial' | 'on-demand' | 'missing' | 'unsupported' | 'blocked' | 'unknown'

export type PlatformCapabilityValue =
  | string
  | boolean
  | null
  | {
      status?: string
      state?: string
      availability?: string
      note?: string
      detail?: string
      source?: string
    }

export type PlatformCapabilityMap = Record<string, PlatformCapabilityValue>

export interface PlatformRegistryPlatform {
  id?: string
  platformId?: string
  slug?: string
  name?: string
  displayName?: string
  label?: string
  status?: string
  storeIds?: string[]
  capabilities?: PlatformCapabilityMap
}

export interface PlatformRegistryStore {
  id?: string
  storeId?: string
  platformId?: string
  platform?: string
  name?: string
  displayName?: string
  officialStoreId?: string | null
  platformStoreId?: string | null
  platformStoreIdState?: string
  status?: string
  capabilities?: PlatformCapabilityMap
}

export interface PlatformRegistry {
  schemaVersion?: string
  generatedAt?: string
  updatedAt?: string
  platforms?: PlatformRegistryPlatform[]
  stores?: PlatformRegistryStore[]
  capabilities?: Record<string, PlatformCapabilityValue | PlatformCapabilityMap>
}

export interface JdMetadataPayload {
  schemaVersion: string
  generatedAt: string
  meta: {
    runId: string
    storeName: string
    minDate: string
    maxDate: string
    dates: string[]
    productCounts: Record<string, number>
    mappedCounts: Record<string, number>
    collectedAt: string
    sourceSystem: string
    pageId: string
    attribution: string
  }
}

export interface JdProductCatalogRecord {
  jdSkuId: string
  spuId: string
  merchantSku: string
  productName: string
  salesAttribute: string
  shortTitle: string
  productUrl: string
  imagePath: string
}

export interface JdProductCatalog {
  schemaVersion: string
  generatedAt: string
  summary: {
    skuCount: number
    spuCount: number
    imageMatchedSkuCount: number
    imageCoveragePercent: number
  }
  records: JdProductCatalogRecord[]
}

export interface ResourceState<T> {
  data: T | null
  loading: boolean
  error: string
  reload: () => Promise<void>
}

export interface NormalizedCapability {
  status: CapabilityAvailability
  label: string
  note: string
}

export interface PlatformCapabilityColumn {
  id: string
  label: string
  aliases: string[]
}

export interface PlatformCapabilityRow {
  key: string
  platformId: string
  platformName: string
  storeId: string
  storeName: string
  officialStoreId: string
  capabilities: Record<string, NormalizedCapability>
}

export interface PinduoduoCatalogProduct {
  productId: string
  name: string
  promotion: PddProductRecord | null
  opportunity: PddMarketingOpportunity | null
  sourceCount: number
}

export interface PinduoduoMasterProduct {
  productId: string
  name: string
  category: string
  merchantProductCode: string
  brand: string
  attributes: string
  status: string
  createdAt: string
  image: string
  images: string[]
  variantCount: number
  mappedVariantCount: number
  merchantVariantCodeCount: number
  totalStock: number
  groupPriceMin: number | null
  groupPriceMax: number | null
}

export interface PinduoduoMasterVariant {
  variantId: string
  productId: string
  productName: string
  merchantVariantCode: string
  merchantProductCode: string
  variantAttributes: string
  color: string
  size: string
  stock: number
  groupPrice: number | null
  singlePrice: number | null
  referencePrice: number | null
  productStatus: string
  variantStatus: string
  previewImage: string
  mappingStatus: 'confirmed' | 'unmapped'
  companyVariantId: string | null
}

export interface PinduoduoProductCatalog {
  schemaVersion: string
  generatedAt: string
  meta: {
    platformId: 'pinduoduo'
    storeId: string
    storeName: string
    sourceSystem: string
    sourceType: string
    sourcePage: string
    exportedAt: string
    sourceFile: string
    sha256: string
    encoding: string
    productCount: number
    variantCount: number
    uniqueMerchantVariantCodeCount: number
    matchedMerchantVariantCodeCount: number
    unmatchedMerchantVariantCodeCount: number
    mappedVariantCount: number
    unmappedVariantCount: number
    caliber: string
  }
  products: PinduoduoMasterProduct[]
  variants: PinduoduoMasterVariant[]
  unmatchedMerchantVariantCodes: string[]
}

export const PLATFORM_CAPABILITY_COLUMNS: PlatformCapabilityColumn[] = [
  { id: 'storeTransaction', label: '全店交易', aliases: ['storeTransaction', 'storeTransactions', 'fullStoreTransaction', 'transaction', 'sales'] },
  { id: 'product', label: '商品级', aliases: ['product', 'products', 'productLevel', 'productData', 'productAnalytics'] },
  { id: 'specification', label: '规格级', aliases: ['specification', 'specifications', 'spec', 'sku', 'skuLevel', 'variantAnalytics'] },
  { id: 'traffic', label: '流量', aliases: ['traffic', 'flow'] },
  { id: 'advertising', label: '广告', aliases: ['advertising', 'advertisement', 'ads', 'promotion'] },
  { id: 'refund', label: '退款', aliases: ['refund', 'refunds', 'afterSales'] },
  { id: 'settlement', label: '结算', aliases: ['settlement', 'settlements', 'finance'] },
  { id: 'profit', label: '利润', aliases: ['profit', 'profits', 'profitability'] },
]

const jsonCache = new Map<string, unknown>()
const inFlightRequests = new Map<string, Promise<unknown>>()

export function invalidatePlatformDataCache(urls?: string[]) {
  if (!urls) {
    jsonCache.clear()
    return
  }
  for (const url of urls) jsonCache.delete(url)
}

async function loadJson<T>(url: string, force = false): Promise<T> {
  if (force) {
    jsonCache.delete(url)
  }
  if (jsonCache.has(url)) return jsonCache.get(url) as T
  const existing = inFlightRequests.get(url)
  if (existing) return existing as Promise<T>

  const request = fetch(url, { cache: 'no-store' })
    .then(async (response) => {
      if (!response.ok) throw new Error(`数据加载失败：${response.status}`)
      return response.json() as Promise<T>
    })
    .then((payload) => {
      jsonCache.set(url, payload)
      return payload
    })
    .finally(() => {
      inFlightRequests.delete(url)
    })

  inFlightRequests.set(url, request)
  return request
}

function useCachedJson<T>(url: string): ResourceState<T> {
  const cached = jsonCache.get(url) as T | undefined
  const [data, setData] = useState<T | null>(() => cached ?? null)
  const [loading, setLoading] = useState(() => !cached)
  const [error, setError] = useState('')

  const request = useCallback(async (force = false) => {
    setLoading(true)
    setError('')
    try {
      const payload = await loadJson<T>(url, force)
      setData(payload)
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    let active = true
    setLoading(!jsonCache.has(url))
    setError('')
    loadJson<T>(url)
      .then((payload) => {
        if (active) setData(payload)
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : String(reason))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [url])

  const reload = useCallback(() => request(true), [request])
  return { data, loading, error, reload }
}

export function usePlatformRegistry(): ResourceState<PlatformRegistry> {
  return useCachedJson<PlatformRegistry>(PLATFORM_REGISTRY_URL)
}

export function useJdMetadata(): ResourceState<JdMetadataPayload> {
  return useCachedJson<JdMetadataPayload>(JD_METADATA_URL)
}

export function useJdProductCatalog(): ResourceState<JdProductCatalog> {
  return useCachedJson<JdProductCatalog>(JD_PRODUCT_CATALOG_URL)
}

export function useJdOrderFacts(): ResourceState<JdOrderFacts> {
  return useCachedJson<JdOrderFacts>(JD_ORDER_FACTS_URL)
}

export function useJdAfterSalesFacts(): ResourceState<JdAfterSalesFacts> {
  return useCachedJson<JdAfterSalesFacts>(JD_AFTER_SALES_FACTS_URL)
}

export function usePinduoduoResource(): ResourceState<PinduoduoData> {
  return useCachedJson<PinduoduoData>(PINDUODUO_DATA_URL)
}

export function usePinduoduoProductCatalog(): ResourceState<PinduoduoProductCatalog> {
  return useCachedJson<PinduoduoProductCatalog>(PINDUODUO_PRODUCT_CATALOG_URL)
}

export function usePinduoduoOrderFacts(): ResourceState<PinduoduoOrderFacts> {
  return useCachedJson<PinduoduoOrderFacts>(PINDUODUO_ORDER_FACTS_URL)
}

export function usePinduoduoAfterSalesFacts(): ResourceState<PinduoduoAfterSalesFacts> {
  return useCachedJson<PinduoduoAfterSalesFacts>(PINDUODUO_AFTER_SALES_FACTS_URL)
}

export function useDouyinLiveAnalysis(storeId = DOUYIN_SHUIJU_STORE_ID): ResourceState<DouyinLiveAnalysis> {
  return useCachedJson<DouyinLiveAnalysis>(douyinLiveAnalysisUrl(storeId))
}

export function useInternalProductInfo(): ResourceState<InternalProductInfo> {
  return useCachedJson<InternalProductInfo>(INTERNAL_PRODUCT_INFO_URL)
}

export function useCompanyProductOverrides(): ResourceState<CompanyProductOverrides> {
  return useCachedJson<CompanyProductOverrides>(COMPANY_PRODUCT_OVERRIDES_URL)
}

export function useProductImageOverrides(): ResourceState<ProductImageOverrides> {
  return useCachedJson<ProductImageOverrides>(PRODUCT_IMAGE_OVERRIDES_URL)
}

export function useErpInventory(): ResourceState<ErpInventory> {
  return useCachedJson<ErpInventory>(ERP_INVENTORY_URL)
}

export function usePlatformProductMappings(): ResourceState<PlatformProductMappings> {
  return useCachedJson<PlatformProductMappings>(PLATFORM_PRODUCT_MAPPINGS_URL)
}

export function useStoreCostProfiles(): ResourceState<StoreCostProfiles> {
  return useCachedJson<StoreCostProfiles>(STORE_COST_PROFILES_URL)
}

export function useCompanyPlatformResources() {
  // 三个公司覆盖资源独立发起，单个失败只更新自己的状态。
  const registry = usePlatformRegistry()
  const jd = useJdMetadata()
  const pinduoduoCatalog = usePinduoduoProductCatalog()
  return { registry, jd, pinduoduoCatalog }
}

export function mergePinduoduoProducts(data: PinduoduoData): PinduoduoCatalogProduct[] {
  const products = new Map<string, PinduoduoCatalogProduct>()
  for (const promotion of data.products) {
    products.set(promotion.productId, {
      productId: promotion.productId,
      name: promotion.name,
      promotion,
      opportunity: null,
      sourceCount: 1,
    })
  }
  for (const opportunity of data.marketingOpportunities) {
    const current = products.get(opportunity.productId)
    if (current) {
      current.opportunity = opportunity
      current.sourceCount = 2
    } else {
      products.set(opportunity.productId, {
        productId: opportunity.productId,
        name: opportunity.name,
        promotion: null,
        opportunity,
        sourceCount: 1,
      })
    }
  }
  return [...products.values()]
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function platformId(platform: PlatformRegistryPlatform): string {
  return platform.id || platform.platformId || platform.slug || 'unknown-platform'
}

function platformName(platform: PlatformRegistryPlatform): string {
  return platform.name || platform.displayName || platform.label || platformId(platform)
}

function storeId(store: PlatformRegistryStore): string {
  return store.id || store.storeId || 'unknown-store'
}

function storePlatformId(store: PlatformRegistryStore): string {
  return store.platformId || store.platform || 'unknown-platform'
}

function storeName(store: PlatformRegistryStore): string {
  return store.name || store.displayName || storeId(store)
}

function capabilityMap(value: unknown): PlatformCapabilityMap | null {
  const record = objectValue(value)
  return record ? record as PlatformCapabilityMap : null
}

function valueFromMap(map: PlatformCapabilityMap | null, aliases: string[]): PlatformCapabilityValue | undefined {
  if (!map) return undefined
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(map, alias)) return map[alias]
  }
  return undefined
}

function registryCapability(
  registry: PlatformRegistry,
  platform: PlatformRegistryPlatform,
  store: PlatformRegistryStore | null,
  aliases: string[],
): PlatformCapabilityValue | undefined {
  const root = registry.capabilities || {}
  const ownerIds = store
    ? [storeId(store), storePlatformId(store), platformId(platform)]
    : [platformId(platform)]
  const candidates: Array<PlatformCapabilityMap | null> = [
    store?.capabilities || null,
    platform.capabilities || null,
    ...ownerIds.map((id) => capabilityMap(root[id])),
  ]
  for (const candidate of candidates) {
    const value = valueFromMap(candidate, aliases)
    if (value !== undefined) return value
  }

  // 同时兼容 capability-first 结构：capabilities.product.jd = {...}。
  for (const alias of aliases) {
    const capabilityOwners = objectValue(root[alias])
    if (!capabilityOwners) continue
    for (const ownerId of ownerIds) {
      if (Object.prototype.hasOwnProperty.call(capabilityOwners, ownerId)) {
        return capabilityOwners[ownerId] as PlatformCapabilityValue
      }
    }
  }
  return undefined
}

export function normalizeCapability(value: PlatformCapabilityValue | undefined): NormalizedCapability {
  const detail = objectValue(value)
  const rawStatus = typeof value === 'boolean'
    ? (value ? 'ready' : 'missing')
    : typeof value === 'string'
      ? value
      : String(detail?.state || detail?.status || detail?.availability || 'unknown')
  const normalized = rawStatus.trim().toLowerCase().replaceAll('_', '-').replaceAll(' ', '-')
  let status: CapabilityAvailability = 'unknown'
  if (['ready', 'full', 'available', 'complete', 'completed', 'yes'].includes(normalized)) status = 'ready'
  else if (['partial', 'limited', 'promotion-only', 'product-only'].includes(normalized)) status = 'partial'
  else if (['on-demand', 'manual', 'paused', 'stale'].includes(normalized)) status = 'on-demand'
  else if (['missing', 'unavailable', 'not-ready', 'not-acquired', 'none', 'no'].includes(normalized)) status = 'missing'
  else if (['not-supported', 'unsupported'].includes(normalized)) status = 'unsupported'
  else if (normalized === 'blocked') status = 'blocked'

  const labels: Record<CapabilityAvailability, string> = {
    ready: '已就绪',
    partial: '部分就绪',
    'on-demand': '按需更新',
    missing: '未取得',
    unsupported: '不支持',
    blocked: '已受阻',
    unknown: '待核实',
  }
  return {
    status,
    label: labels[status],
    note: String(detail?.note || detail?.detail || detail?.source || ''),
  }
}

export function buildCapabilityRows(registry: PlatformRegistry): PlatformCapabilityRow[] {
  const platforms = registry.platforms || []
  const stores = registry.stores || []
  const rows: PlatformCapabilityRow[] = []

  for (const platform of platforms) {
    const currentPlatformId = platformId(platform)
    const platformStores = stores.filter((store) => storePlatformId(store) === currentPlatformId)
    const rowStores = platformStores.length ? platformStores : [null]
    for (const store of rowStores) {
      const currentStoreId = store ? storeId(store) : ''
      rows.push({
        key: `${currentPlatformId}:${currentStoreId || 'platform'}`,
        platformId: currentPlatformId,
        platformName: platformName(platform),
        storeId: currentStoreId,
        storeName: store ? storeName(store) : '平台尚未登记店铺',
        officialStoreId: store?.platformStoreId || store?.officialStoreId || '',
        capabilities: Object.fromEntries(PLATFORM_CAPABILITY_COLUMNS.map((column) => [
          column.id,
          normalizeCapability(registryCapability(registry, platform, store, column.aliases)),
        ])),
      })
    }
  }

  // 注册表结构异常时保留空状态，不静默伪造店铺行。
  return rows
}
